/**
 * Trajectory read hook.
 *
 * Reads the latest `TrajectorySnapshot` for `(siteId, mapName)` from
 * the shared TanStack Query cache populated by `useLiveData`'s broad
 * SSE subscription. The previous MQTT version registered its own
 * subscription; the Phase 0 Zenoh model has every per-entity hook
 * read from the unified dispatcher-driven cache slice so a single SSE
 * connection serves every consumer (matching the Tide 6-conn/origin
 * browser cap).
 *
 * Returns `null` while no snapshot has arrived yet. Renders the map
 * immediately once the cache is populated.
 *
 * This is a real observer (`useQuery`) with an explicit, infinite
 * `gcTime`, not a bare `getQueryData` read, and the difference is
 * load-bearing during a live-stream stall:
 *
 *   - `useLiveData` writes the snapshot with `setQueryData`, which
 *     builds the query with the DEFAULT `gcTime` (5 min) and starts
 *     its GC timer at creation; later writes never reschedule it
 *     (query-core `Query.setData` does not touch the timer — only
 *     `addObserver` clears it and `removeObserver`/fetch settle
 *     re-arm it). With no observer the slot was removed 5 min after
 *     it was created, whatever had been written since.
 *   - Between writes the map kept rendering whatever the slot held —
 *     a frozen-but-honest picture — but once the slot was collected
 *     the read returned `null` and the paths vanished, which is
 *     indistinguishable in the UI from "the robot has no itinerary".
 *     A platform-plane wedge (zenoh-router-platform ↔ zenoh-webserver)
 *     stalls the whole stream for minutes at a time, so this is
 *     exactly the moment the snapshot must NOT disappear.
 *
 * Holding an observer clears the GC timer while the map is mounted;
 * the explicit `gcTime` keeps the slot when the map unmounts too, so
 * a stall freezes the last snapshot rather than blanking it, and the
 * top-bar "trajectory feed stale" chip / "Disconnected" dot say why.
 * The slot is dropped only on site change (`useLiveData` removes every
 * `TRAJECTORIES_KEY` query), never by the collector — one snapshot per
 * (site, level) is small.
 *
 * `skipToken` makes the query permanently non-fetching: there is no
 * REST endpoint for trajectories, the cache is fed by the stream only,
 * and a refetch (window focus, `invalidateQueries`) must never replace
 * the snapshot with anything.
 */
import { skipToken, useQuery } from '@tanstack/react-query';
import { trajectoriesKey } from '../scope';
import type { TrajectorySnapshot } from '../ws-types';

/** See the module comment: never collected, only removed on site change. */
export const TRAJECTORY_SNAPSHOT_GC_TIME_MS = Number.POSITIVE_INFINITY;

export function useTrajectories(siteId: string, mapName: string | null): TrajectorySnapshot | null {
  const enabled = Boolean(siteId && mapName);
  const query = useQuery<TrajectorySnapshot | null>({
    queryKey: trajectoriesKey(siteId, mapName ?? ''),
    queryFn: skipToken,
    gcTime: TRAJECTORY_SNAPSHOT_GC_TIME_MS,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return enabled ? (query.data ?? null) : null;
}
